import { Component, OnInit, forwardRef, Input, ElementRef, ChangeDetectorRef, Output, EventEmitter, Optional } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ControlContainer, FormGroup, FormControl } from '@angular/forms';
import { parse, isValid, format } from 'date-fns';

@Component({
  selector: 'viva-date-input',
  templateUrl: './viva-date-input.component.html',
  styleUrls: ['./viva-date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivaDateInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class VivaDateInputComponent implements OnInit, ControlValueAccessor {
  @Input() control: AbstractControl | null = null;
  @Input() label?: string;
  @Input() formControlName: string | number | null = null;
  @Input() placeholder?: string;
  @Input() iconStartSrc?: string;
  @Input() iconEndSrc?: string;
  @Input() required?: string;
  @Input() maxlength?: string;
  @Input() minlength?: string;
  @Input() inputmode?: string;
  @Input() autocomplete?: string;
  @Input() onkeydown?: (event: KeyboardEvent) => boolean;
  @Input() useUSFormat: boolean = false; // US = MM/DD/YYYY, Else = YYYY/MM/DD

  @Input() value: any = '';
  @Output() valueChange = new EventEmitter<any>();
  @Output() changeEvent = new EventEmitter<Event>();

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };
  private nativeElement: HTMLIonInputElement;
  private _dtValue?: string;
  isFocused: boolean = false;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef
  ) {
    this.nativeElement = elRef.nativeElement as HTMLIonInputElement;
  }

  ngOnInit(): void {
    if (!this.control && this.formControlName !== null && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup;
      if (formGroup) {
        this.control = formGroup.get(String(this.formControlName)) as FormControl;
      }
    }
  }

  writeValue(value: any): void {
    if (typeof value === 'string') {
      let dateString = value;

      // If value includes time portion, strip it
      if (value.includes('T')) {
        dateString = value.split('T')[0]; // Keep only YYYY-MM-DD
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const date = VivaDateInputComponent.parseISODateString(dateString);
        this.value = !isNaN(date.getTime())
          ? VivaDateInputComponent.formatDate(date, this.useUSFormat)
          : '';

      } else {
        this.value = value; // fallback for other formats if necessary
      }
    } else {
      this.value = '';
    }

    this._dtValue = this.value;
    this.cdr.detectChanges();

  }

  public static parseISODateString(isoString: string): Date {
    if (!isoString) return new Date(NaN); // handle null or empty safely

    // Only strip the time if present
    const datePart = isoString.includes('T') ? isoString.split('T')[0] : isoString;

    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      console.warn('Invalid date format:', isoString);
      return new Date(NaN); // invalid
    }

    const result = new Date(year, month - 1, day); // local date (no TZ shift)
    return result;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.nativeElement.setAttribute('disabled', 'true');
    } else {
      this.nativeElement.removeAttribute('disabled');
    }
  }


  private formatDebounceTimer?: any;

  onInputChange(event: any): void {
    const inputVal: string = event?.target?.value ?? '';
    const slashCount = (inputVal.match(/\//g) || []).length;
    const shouldDebounce = slashCount >= 2;

    // Store the raw input (formatted or not)
    this._dtValue = inputVal;
    this.value = inputVal;
    this.valueChange.emit(inputVal);

    // Clear any previous debounce timer
    if (this.formatDebounceTimer) {
      clearTimeout(this.formatDebounceTimer);
    }

    if (shouldDebounce) {
      // Debounce reformatting if 2 or more slashes exist (user is editing a nearly complete date)
      this.formatDebounceTimer = setTimeout(() => {
        this.applyFormattedInput(inputVal);
      }, 1500);
    } else {
      // Apply formatting immediately otherwise
      this.applyFormattedInput(inputVal);
    }

    this.changeEvent.emit(event);
    this.cdr.detectChanges();
  }

  private applyFormattedInput(rawInput: string): void {
    let raw = rawInput.replace(/\D/g, '').slice(0, 8); // Only digits, max 8
    let formatted = '';

    if (this.useUSFormat) {
      if (raw.length >= 2) formatted = raw.slice(0, 2);
      if (raw.length >= 4) formatted += '/' + raw.slice(2, 4);
      if (raw.length >= 5) formatted += '/' + raw.slice(4);
    } else {
      if (raw.length >= 4) formatted = raw.slice(0, 4);
      if (raw.length >= 6) formatted += '/' + raw.slice(4, 6);
      if (raw.length >= 7) formatted += '/' + raw.slice(6);
    }

    this._dtValue = formatted;
    this.value = formatted;
    this.valueChange.emit(this.value);

    const serverFormat = this.formatForServer();
    if (serverFormat && this.onChange) {
      this.onChange(serverFormat);
    } else {
      console.warn('⚠️ Parsing failed in applyFormattedInput(). Raw:', this._dtValue);
    }

    this.cdr.detectChanges();
  }

  private formatForServer(): string | null {
    const formatStr = this.useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd';
    const parsed = parse(this._dtValue ?? '', formatStr, new Date());

    var result = isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;


    return result;
  }

  onInputBlur(): void {
    this.onTouched();
    this.isFocused = false;

    this.cdr.detectChanges();
  }

  isValidDate(dateString?: string): boolean {
    if (this.required === 'true' && !dateString) return false;
    if (!dateString) return true;

    const format = this.useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd';
    const parsed = parse(dateString, format, new Date());

    return isValid(parsed);
  }

  private isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  public static formatDate(date: Date, useUSFormat: boolean): string {
    if (!date || !isValid(date)) return '';
    const fmt = useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd';
    return format(date, fmt);
  }

  public static formatISODate(date: Date): string {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  }

  public static parseDateString(dateStr: string, useUSFormat: boolean): Date | null {
    if (!dateStr) return null;

    let format: string;

    if (dateStr.includes('-')) {
      // ISO format
      format = 'yyyy-MM-dd';
    } else if (dateStr.includes('/')) {
      format = useUSFormat ? 'MM/dd/yyyy' : 'yyyy/MM/dd';
    } else {
      console.warn('Unknown date format:', dateStr);
      return null;
    }

    const parsed = parse(dateStr, format, new Date());

    if (!isValid(parsed)) {
      return null;
    }

    return parsed;
  }


  get isInvalid(): boolean {
    const isFormatInvalid = !this.isValidDate(this._dtValue);
    return this.control ? (this.control.invalid && (this.control.dirty || this.control.touched)) || isFormatInvalid : isFormatInvalid;
  }

  get labelWithFormat(): string | undefined {
    if (!this.label)
      return this.label;
    return this.label + this.dateFormat;
  }

  get dateFormat(): string {
    if (this.useUSFormat)
      return ' mm/dd/yyyy';
    else
      return ' yyyy/mm/dd';
  }

  get labelText(): string | undefined {
    return this.labelWithFormat && this._dtValue ? this.labelWithFormat : undefined;
  }

  get actualPlaceholder(): string | undefined {
    return this.dateFormat;
  }
}
